// Single HTTP client for the SyncVault API. Relies on browser-managed 
// HttpOnly cookies for authentication (accessToken and refreshToken).

import { localDB } from './db';

const BASE = import.meta.env.VITE_API_BASE || '/api';
console.log(`[api] Resolved API base URL: ${BASE}`);

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (success) => {
  refreshSubscribers.forEach((cb) => cb(success));
  refreshSubscribers = [];
};

const request = async (path, { method = 'GET', body, headers = {}, isForm = false, _retry = false, signal } = {}) => {
  console.log(`[api] Request: ${method} ${path}`);
  const finalHeaders = { Accept: 'application/json', ...headers };
  if (!isForm && body !== undefined) finalHeaders['Content-Type'] = 'application/json';

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: finalHeaders,
    body: isForm ? body : (body === undefined ? undefined : JSON.stringify(body)),
    signal,
    credentials: 'same-origin', // Crucial: automatically sends and receives HttpOnly cookies
  });

  if (res.status === 204) return null;
  
  // Handle 401 Unauthorized - attempt silent refresh
  const isRefreshPath = path === '/auth/refresh';
  if (res.status === 401 && !_retry && !isRefreshPath && navigator.onLine) {
    if (!isRefreshing) {
      isRefreshing = true;
      try {
        const refreshRes = await request('/auth/refresh', { method: 'POST' });
        const { accessExpiresIn } = refreshRes.data;

        // Calculate new expiry (timestamp)
        const tokenExpiry = Date.now() + (accessExpiresIn * 1000);

        // Update IndexedDB (for persistence/offline grace period)
        const session = await localDB.getSession();
        if (session) {
          await localDB.setSession({
            ...session,
            tokenExpiry
          });
        }

        isRefreshing = false;
        onRefreshed(true);
      } catch (err) {
        isRefreshing = false;
        await localDB.clearSession();
        onRefreshed(false);
        throw err;
      }
    }

    return new Promise((resolve, reject) => {
      subscribeTokenRefresh((success) => {
        if (!success) return reject(new Error('token_refresh_failed'));
        resolve(request(path, { method, body, headers, isForm, _retry: true }));
      });
    });
  }

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const message = data?.error || `request_failed_${res.status}`;
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }
  return data;
};

export const api = {
  // Auth
  login:  (body) => request('/auth/login',  { method: 'POST', body }),
  me:     ()     => request('/auth/me'),
  refresh: () => request('/auth/refresh', { method: 'POST' }),
  logout: () => request('/auth/logout', { method: 'POST' }),

  // Clipboard
  listClips:   (params = '', signal) => request(`/clipboard${params ? `?${params}` : ''}`, { signal }),
  createClip:  (body)         => request('/clipboard', { method: 'POST', body }),
  updateClip:  (id, body)     => request(`/clipboard/${id}`, { method: 'PUT', body }),
  deleteClip:  (id)           => request(`/clipboard/${id}`, { method: 'DELETE' }),

  // Snaps
  listSnaps:   (params = '', signal) => request(`/snaps${params ? `?${params}` : ''}`, { signal }),
  uploadSnap:  (formData)    => request('/snaps', { method: 'POST', body: formData, isForm: true }),
  deleteSnap:  (id)          => request(`/snaps/${id}`, { method: 'DELETE' }),
  snapFileUrl: (id)          => `${BASE}/snaps/${id}/file`,

  // Search
  search:      (q) => request(`/search?q=${encodeURIComponent(q)}`),

  // Devices
  listDevices:   () => request('/devices'),
  registerDevice: (body) => request('/devices/register', { method: 'POST', body }),
  deleteDevice:   (id)   => request(`/devices/${id}`, { method: 'DELETE' }),

  // Notes
  listFolders:   ()           => request('/folders'),
  createFolder:  (body)       => request('/folders', { method: 'POST', body }),
  updateFolder:  (id, body)   => request(`/folders/${id}`, { method: 'PUT', body }),
  deleteFolder:  (id)         => request(`/folders/${id}`, { method: 'DELETE' }),

  listNotes:     (params = '') => request(`/notes${params ? `?${params}` : ''}`),
  getNote:       (id)          => request(`/notes/${id}`),
  createNote:    (body)        => request('/notes', { method: 'POST', body }),
  updateNote:    (id, body)    => request(`/notes/${id}`, { method: 'PUT', body }),
  deleteNote:    (id)          => request(`/notes/${id}`, { method: 'DELETE' }),

  // Admin
  adminStats:    () => request('/admin/stats'),
  adminUsers:    () => request('/admin/users'),
  adminCreateUser: (body) => request('/admin/users', { method: 'POST', body }),
  adminDeleteUser: (id)   => request(`/admin/users/${id}`, { method: 'DELETE' }),
  adminLogs:     (limit = 100) => request(`/admin/logs?limit=${limit}`),

  // Shared Clips
  listSharedClips:   (params = '') => request(`/shared${params ? `?${params}` : ''}`),
  sendSharedClip:    (body)       => request('/shared', { method: 'POST', body }),
  deleteSharedClip:  (id)         => request(`/shared/${id}`, { method: 'DELETE' }),
  clearChat:         (contactId)  => request(`/shared?withUser=${contactId}`, { method: 'DELETE' }),
  listUsers:         ()           => request('/shared/users'),
};
